/**
 * CampusCursoVinculadoConfig: dropdown para vincular un espacio del Campus con
 * un curso de Capacitaciones. Al vincular, se pueden emitir constancias y
 * certificados directamente desde los Resultados del examen.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link2, Loader2, Save, Unlink } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function CampusCursoVinculadoConfig({ spaceId }) {
  const [cursos, setCursos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [current, setCurrent] = useState(null); // curso_vinculado_id actual
  const [selected, setSelected] = useState('');
  const [error, setError] = useState(null);

  const headers = () => ({
    Authorization: `Bearer ${localStorage.getItem('token')}`,
  });

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [spaceRes, cursosRes] = await Promise.all([
        axios.get(`${API_URL}/campus/espacios/${spaceId}`, { headers: headers() }),
        axios.get(`${API_URL}/capacitaciones/admin/cursos`, { headers: headers() }),
      ]);
      const cursoActual = spaceRes.data?.curso_vinculado_id || null;
      setCurrent(cursoActual);
      setSelected(cursoActual || '');
      const list = Array.isArray(cursosRes.data) ? cursosRes.data : (cursosRes.data?.cursos || []);
      setCursos(list);
    } catch (err) {
      setError(err.response?.data?.detail || 'No se pudieron cargar los cursos.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [spaceId]);

  const guardar = async () => {
    setSaving(true);
    setError(null);
    try {
      const payload = { curso_vinculado_id: selected || null };
      await axios.put(`${API_URL}/campus/espacios/${spaceId}`, payload, { headers: headers() });
      setCurrent(selected || null);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const desvincular = async () => {
    if (!window.confirm('¿Desvincular este espacio del curso de Capacitaciones? Se ocultarán los botones de emitir constancia/certificado.')) return;
    setSelected('');
    setSaving(true);
    setError(null);
    try {
      await axios.put(`${API_URL}/campus/espacios/${spaceId}`, { curso_vinculado_id: null }, { headers: headers() });
      setCurrent(null);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al desvincular');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card><CardContent className="p-4 flex items-center gap-2 text-sm text-gray-500">
        <Loader2 className="w-4 h-4 animate-spin" /> Cargando…
      </CardContent></Card>
    );
  }

  const dirty = (selected || null) !== (current || null);
  const cursoActualObj = current ? cursos.find(c => c.id === current) : null;

  return (
    <Card>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center gap-2">
          <Link2 className="w-5 h-5 text-violet-600" />
          <h3 className="font-semibold">Vincular con curso de Capacitaciones</h3>
        </div>
        <p className="text-xs text-gray-500">
          Cuando este espacio del Campus está vinculado a un curso de Capacitaciones, en la vista de
          <strong> Resultados de exámenes</strong> podés emitir <strong>Constancia</strong> y <strong>Certificado</strong>
          directamente para cada participante, y editar su <strong>nota</strong>.
        </p>

        {current && cursoActualObj && (
          <div className="text-xs bg-violet-50 border border-violet-200 rounded-md px-3 py-2 flex items-center justify-between">
            <span>
              Actualmente vinculado a: <strong>{cursoActualObj.nombre}</strong>
              {cursoActualObj.slug && <span className="text-gray-500"> ({cursoActualObj.slug})</span>}
            </span>
          </div>
        )}

        <div>
          <label className="text-xs font-medium text-gray-600 uppercase mb-1 block">Curso</label>
          <select
            value={selected}
            onChange={(e) => setSelected(e.target.value)}
            className="w-full h-10 border border-gray-300 rounded px-2 text-sm"
            disabled={saving}
            data-testid="curso-vinculado-select"
          >
            <option value="">— Sin vincular —</option>
            {cursos.map((c) => (
              <option key={c.id} value={c.id}>
                {c.nombre}{c.slug ? ` · ${c.slug}` : ''}
              </option>
            ))}
          </select>
        </div>

        {error && (
          <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded px-3 py-1.5">
            {error}
          </div>
        )}

        <div className="flex gap-2">
          <Button
            onClick={guardar}
            disabled={!dirty || saving}
            data-testid="curso-vinculado-save"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
            Guardar vínculo
          </Button>
          {current && (
            <Button
              variant="outline"
              onClick={desvincular}
              disabled={saving}
              data-testid="curso-vinculado-unlink"
            >
              <Unlink className="w-4 h-4 mr-1" /> Desvincular
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
